import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "../../site/ui";
import { fmtDate } from "../../site/pages/Forum";
import { LAONA_CATEGORIES } from "../data";

/* 자료실 카드 — 소개 화면의 '최근 자료'도 함께 쓴다 */
export function PostCard({ post }) {
  /* 레시피는 비회원도 보는 갤러리 주소로 */
  const to = post.category === "laona-recipe" ? `/laonabot/recipes/${post.id}` : `/laonabot/library/${post.id}`;
  return (
    <Link to={to} className="lb-card">
      {post.cover ? (
        <div className="lb-card__thumb"><img src={post.cover} alt="" loading="lazy" /></div>
      ) : (
        <div className="lb-card__thumb lb-card__thumb--empty" aria-hidden="true" />
      )}
      <div className="lb-card__body">
        <div className="lb-card__tags">
          {post.draft && <span className="lb-tag lb-tag--draft">비공개</span>}
          {post.pinned && <span className="lb-tag lb-tag--pin">공지</span>}
          <span className="lb-tag">{post.category_label}</span>
        </div>
        <h3 className="lb-card__title">{post.title}</h3>
        {post.summary && <p className="lb-card__sum">{post.summary}</p>}
        <div className="lb-card__meta">
          <span>{fmtDate(post.created_at)}</span>
          {post.attachments > 0 && <span>첨부 {post.attachments}</span>}
          <span>조회 {post.views.toLocaleString("ko-KR")}</span>
        </div>
      </div>
    </Link>
  );
}

export default function LaonaLibrary() {
  const [params, setParams] = useSearchParams();
  const cat = LAONA_CATEGORIES.some((c) => c.id === params.get("cat")) ? params.get("cat") : "";
  const catInfo = LAONA_CATEGORIES.find((c) => c.id === cat);
  const q = params.get("q") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  useSeo({
    title: catInfo ? `${catInfo.label} — LaonaBot 자료실` : "자료실 — LaonaBot",
    description: "라오나봇 조립 가이드, 모듈 핀맵, 3D 케이스 STL, 펌웨어를 내려받을 수 있습니다.",
    path: cat ? `/laonabot/library?cat=${cat}` : "/laonabot/library",
  });

  const [draft, setDraft] = useState(q);
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => { setDraft(q); }, [q]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: "loading" }));
    const qs = new URLSearchParams({ site: "laona" });
    if (cat) qs.set("category", cat);
    if (q) qs.set("q", q);
    if (page > 1) qs.set("page", String(page));
    fetch(`/api/forum?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => alive && setState({ status: "ready", data }))
      .catch(() => alive && setState({ status: "error", data: null }));
    return () => { alive = false; };
  }, [cat, q, page]);

  /* 조건을 바꾸면 1쪽부터 — 비어 있는 값은 주소에서 뺀다 */
  function go(next) {
    const merged = { cat, q, page: 1, ...next };
    const out = {};
    if (merged.cat) out.cat = merged.cat;
    if (merged.q) out.q = merged.q;
    if (merged.page > 1) out.page = String(merged.page);
    setParams(out);
  }

  const data = state.data;
  const count = (id) => data?.categories.find((c) => c.id === id)?.count;

  return (
    <section className="lb-section lb-section--top">
      <div className="lb-wrap">
        <header className="lb-head lb-head--row">
          <div>
            <p className="lb-kicker">LIBRARY{catInfo ? ` / ${catInfo.label}` : ""}</p>
            <h1>{catInfo ? catInfo.label : "자료실"}</h1>
            <p className="lb-lead">
              {catInfo ? `${catInfo.desc} 자료입니다.` : "조립 가이드 · 모듈 핀맵 · 3D 케이스 · 펌웨어를 공개합니다."}
            </p>
          </div>
          <div className="lb-head__tools">
            <form className="lb-search" role="search" onSubmit={(e) => { e.preventDefault(); go({ q: draft.trim() }); }}>
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="제목 · 내용 검색"
                aria-label="자료 검색"
              />
              <button type="submit" className="lb-btn lb-btn--primary">검색</button>
            </form>
            {/* 관리자에게만 — 서버가 목록 응답에 admin 을 실어 준다 */}
            {data?.admin && <Link to="/laonabot/library/new" className="lb-btn lb-btn--primary">+ 자료 올리기</Link>}
          </div>
        </header>

        <div className="lb-tabs" role="tablist" aria-label="분류">
          <button type="button" role="tab" aria-selected={!cat} className="lb-tabs__tab" onClick={() => go({ cat: "" })}>
            전체{data ? ` ${data.total_all}` : ""}
          </button>
          {LAONA_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={cat === c.id}
              className="lb-tabs__tab"
              onClick={() => go({ cat: c.id })}
            >
              {c.label}{count(c.id) != null ? ` ${count(c.id)}` : ""}
            </button>
          ))}
        </div>

        {q && (
          <p className="lb-result">
            ‘{q}’ 검색 결과 {data ? data.total : "…"}건{" "}
            <button type="button" className="lb-linkbtn" onClick={() => go({ q: "" })}>검색 지우기</button>
          </p>
        )}

        {state.status === "error" && <p className="lb-empty">자료를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>}
        {state.status === "loading" && !data && <p className="lb-empty">자료를 불러오는 중입니다…</p>}
        {data && data.items.length === 0 && (
          <div className="lb-empty lb-empty--box">
            <b>{q ? "검색 결과가 없습니다" : "아직 올라온 자료가 없습니다"}</b>
            <p>{q ? "다른 낱말로 검색해 보세요." : "자료가 준비되는 대로 이곳에 올라옵니다."}</p>
          </div>
        )}
        {data && data.items.length > 0 && (
          <div className={`lb-cards ${state.status === "loading" ? "is-loading" : ""}`}>
            {data.items.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}

        {data && data.pages > 1 && (
          <nav className="lb-pager" aria-label="쪽 이동">
            <button type="button" className="lb-btn" disabled={page <= 1} onClick={() => go({ page: page - 1 })}>이전</button>
            <span>{page} / {data.pages}</span>
            <button type="button" className="lb-btn" disabled={page >= data.pages} onClick={() => go({ page: page + 1 })}>다음</button>
          </nav>
        )}
      </div>
    </section>
  );
}
